import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';

import { AppSidebar } from '@/components/app-sidebar';
import { AuthScreen } from '@/components/auth-screen';
import { NoWorkspace } from '@/components/no-workspace';
import { PageHeader, PageHeaderProvider } from '@/components/page-header';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { ThemeProvider, useTheme } from '@/lib/theme';

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthGate />
        <Toasts />
      </AuthProvider>
    </ThemeProvider>
  );
}

/**
 * Sonner reads its theme from next-themes, which this app does not use — its
 * own `ThemeProvider` owns the `.dark` class. Left to itself the toaster would
 * fall back to `system` and ignore an explicit light/dark choice, so the
 * resolved theme is passed in.
 */
function Toasts() {
  const { theme } = useTheme();

  return <Toaster theme={theme} />;
}

/**
 * Gate the console behind a session.
 *
 * A rendering decision, not a security boundary: an unauthenticated client that
 * skipped this would still get nothing back from PostgREST.
 */
function AuthGate() {
  const { session, claims, isLoading } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  /**
   * Routes under /auth are the ones you reach *without* a session — the email
   * confirmation link most of all. Sending them to the sign-in screen instead
   * would swallow the token and leave the account unconfirmed, which is exactly
   * what happened before this check existed.
   */
  if (pathname.startsWith('/auth/')) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex min-h-svh items-center justify-center">
        Loading…
      </div>
    );
  }

  if (!session) {
    return <AuthScreen />;
  }

  /**
   * Accepting an invitation happens outside the console chrome: the invitee is
   * signed in but not yet a member of anything, so the sidebar would render
   * empty behind a screen that exists to fix exactly that.
   */
  if (pathname === '/join') {
    return <Outlet />;
  }

  /**
   * A session with no team membership has nothing to operate on. The shell
   * would render an empty sidebar around an empty dashboard; say why instead.
   */
  if (claims.teamAccountIds.length === 0 && !claims.isSuperAdmin) {
    return <NoWorkspace />;
  }

  return <Shell />;
}

function Shell() {
  return (
    /*
     * One header for the whole console. Detail routes add their crumb through
     * `useDetailTitle` rather than rendering a header of their own, so the
     * sticky bar never doubles up or shifts between a list and a record.
     */
    <PageHeaderProvider>
      <SidebarProvider>
        <AppSidebar />

        {/*
          `min-w-0` so a wide child cannot push the console sideways.

          A flex item defaults to `min-width: auto`, meaning it will not shrink
          below its content's minimum width. The inset sits beside the sidebar
          in a flex row, so a data table wide enough to scroll made the whole
          shell outgrow the viewport: the page scrolled horizontally and took
          the sidebar off-screen with it, instead of the table scrolling inside
          its own container. Needed on both the inset and the main below it --
          the constraint propagates through every flex item in the chain.
        */}
        <SidebarInset className="min-w-0">
          <PageHeader />

          <main className="flex min-w-0 flex-1 flex-col gap-4 p-4">
            <Outlet />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </PageHeaderProvider>
  );
}
